import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from './ConfirmDialog';
import { errorMessage } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * Safer status changes (quotes and jobs): the current status is a badge, only the usual next step(s) are
 * buttons, and every change is confirmed first. Any other status (including going back) is reachable through
 * "Change…", a two-step dialog. After a change the toast offers Undo.
 *
 * Props: status, order (all statuses), styles (status -> { color, bg }), labelOf(status),
 * nextSteps [{ to, label, color, variant, icon }], confirmOf(to) -> { title, text, confirmLabel },
 * successOf(to) -> toast text, onChange(to) -> Promise, locked (no changes; `lockedHint` explains why).
 * The ref exposes request(to), e.g. for the checklist's "Mark as done" button.
 */
const StatusFlow = forwardRef(function StatusFlow(
    { status, order, styles, labelOf, nextSteps = [], confirmOf, successOf, onChange, locked, lockedHint }, ref) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [pending, setPending] = useState(null);      // status waiting for confirmation
    const [picker, setPicker] = useState(null);        // selected status in the "Change…" dialog, or null when closed

    const apply = async (to, from) => {
        try {
            await onChange(to);
            showToast(successOf(to), 'success', from ? {
                action: { label: t('status.undo'), onClick: () => apply(from, null) },
            } : undefined);
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        }
    };

    const request = (to) => { if (!locked && to !== status) setPending(to); };
    useImperativeHandle(ref, () => ({ request }));

    const confirm = async () => {
        const to = pending;
        await apply(to, status);
        setPending(null);
    };

    const s = styles[status] || {};
    const c = pending ? confirmOf(pending) : {};

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: 999, bgcolor: s.bg }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: s.color }}>{labelOf(status)}</Typography>
            </Box>
            {!locked && nextSteps.map(step => (
                <Button key={step.to} size="small" variant={step.variant || 'contained'} color={step.color || 'primary'} disableElevation
                        startIcon={step.icon} onClick={() => request(step.to)}>
                    {step.label}
                </Button>
            ))}
            {!locked && (
                <Button size="small" onClick={() => setPicker(order.find(x => x !== status))} sx={{ color: '#64748B', fontWeight: 500 }}>
                    {t('status.changeLink')}
                </Button>
            )}
            {locked && lockedHint && <Typography sx={{ fontSize: 12.5, color: '#94A3B8' }}>{lockedHint}</Typography>}

            {/* Any status, in two deliberate steps: pick, then confirm. */}
            <Dialog open={picker != null} onClose={() => setPicker(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>{t('status.changeTitle')}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13.5, color: '#64748B', mb: 1 }}>{t('status.current', { status: labelOf(status) })}</Typography>
                    <RadioGroup value={picker ?? ''} onChange={(e) => setPicker(e.target.value)}>
                        {order.filter(x => x !== status).map(x => (
                            <FormControlLabel key={x} value={x} control={<Radio size="small" />} label={(
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: styles[x]?.color }} />
                                    <Typography sx={{ fontSize: 14 }}>{labelOf(x)}</Typography>
                                </Box>
                            )} />
                        ))}
                    </RadioGroup>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setPicker(null)} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                    <Button variant="contained" disableElevation disabled={!picker} onClick={() => { const to = picker; setPicker(null); request(to); }}>
                        {t('status.next')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog open={!!pending} danger={false} title={c.title} text={c.text} confirmLabel={c.confirmLabel}
                           onConfirm={confirm} onClose={() => setPending(null)} />
        </Box>
    );
});

export default StatusFlow;
